import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { ACHIEVEMENTS, evaluateAchievements } from '../achievements';
import { audio } from '../game/audio/AudioEngine';
import { useGameStore } from '../store/gameStore';
import { ComboBadge } from './ComboBadge';
import { DailyWinnerClock } from './DailyWinnerClock';
import { DeathModal } from './DeathModal';
import { ReconnectingModal } from './ReconnectingModal';
import { MilestoneToast } from './MilestoneToast';
import { SurvivalToast } from './SurvivalToast';
import { HapticsHub } from './HapticsHub';
import { DeathRecapOverlay } from './DeathRecapOverlay';
import { FirstPlayTutorial } from './FirstPlayTutorial';
import { IdentityCard } from './IdentityCard';
import { Leaderboard } from './Leaderboard';
import { KillFeed } from './KillFeed';
import { RewardPokeToast } from './RewardPokeToast';
import { useDailyChallengeTracker } from './useDailyChallengeTracker';

/**
 * HUD — thin composer over Framer-Motion-driven sub-components.
 *
 * Layout:
 *   top-left:     IdentityCard (color dot, spring count-up mass, boost meter, best)
 *   below it:     ComboBadge (AnimatePresence, springs in per combo step)
 *   top-right:    Leaderboard (layout springs for rank reorder)
 *   bottom-left:  FPS + mute toggle
 *   right-mid:    Controls hint
 *   center:       DeathModal (stagger-in, GlowButton)
 *   center:       Pause overlay (AnimatePresence on tab blur)
 */
export default function HUD({ onHome }: { onHome?: () => void } = {}) {
  const {
    fps, mass, alive, playerHue, combo, bestMass, muted, paused, audioLocked,
    surgeRemaining, doubleRemaining, shielded, kills,
    comboRemaining, killedByName, killedByHue,
    recapActive, recapRank,
  } = useGameStore((s) => s.hud);
  // Total alive snakes at the moment of death — drives the
  // "#7 מתוך 30" rank display in DeathModal. Derived from the live
  // leaderboard so it doesn't need a separate engine push.
  const leaderboard = useGameStore((s) => s.leaderboard);
  const respawn = useGameStore((s) => s.respawn);
  const toggleMute = useGameStore((s) => s.toggleMute);
  const playerName = useGameStore((s) => s.playerName);
  // Daily-challenge progress tracker — pure subscriber, lives outside
  // the engine so the engine stays single-responsibility.
  useDailyChallengeTracker();

  const pushMatch = useGameStore((s) => s.pushMatch);
  const unlockedAchievements = useGameStore((s) => s.unlockedAchievements);
  const unlockAchievements = useGameStore((s) => s.unlockAchievements);
  const lastAchievement = useGameStore((s) => s.lastAchievement);
  const clearLastAchievement = useGameStore((s) => s.clearLastAchievement);
  const matchesPlayed = useGameStore((s) => s.matchHistory.length);

  // Persist match history on the alive=true→false edge. Ref guards against
  // re-saving while the player is still dead between renders.
  const wasAlive = useRef(alive);
  useEffect(() => {
    if (wasAlive.current && !alive) {
      pushMatch({ endedAt: Date.now(), mass: Math.round(mass), kills, hue: playerHue });
    }
    wasAlive.current = alive;
  }, [alive, mass, kills, playerHue, pushMatch]);

  // Evaluate achievements on every meaningful state change. The store
  // dedupes already-unlocked ids, so calling this is cheap.
  useEffect(() => {
    const ctx = {
      kills,
      mass,
      bestMass,
      combo,
      matchesPlayed: matchesPlayed + (alive ? 0 : 1),
    };
    const fresh = evaluateAchievements(ctx, new Set(unlockedAchievements));
    if (fresh.length) unlockAchievements(fresh);
  }, [kills, mass, bestMass, combo, alive, matchesPlayed, unlockedAchievements, unlockAchievements]);

  // Auto-clear the toast after a few seconds. Also play a tiny chord on
  // unlock — uses the rival hue of the achievement glyph for the chord
  // root so each unlock feels color-coded.
  useEffect(() => {
    if (!lastAchievement) return;
    const ach = ACHIEVEMENTS.find((a) => a.id === lastAchievement);
    if (ach) audio.eatPerfect(ach.hue);
    const t = setTimeout(clearLastAchievement, 3500);
    return () => clearTimeout(t);
  }, [lastAchievement, clearLastAchievement]);

  const toastAch = lastAchievement
    ? ACHIEVEMENTS.find((a) => a.id === lastAchievement)
    : null;

  return (
    <div className="pointer-events-none absolute inset-0 font-mono text-xs text-white/85">
      <IdentityCard
        mass={mass}
        bestMass={bestMass}
        playerHue={playerHue}
        playerName={playerName}
        surgeRemaining={surgeRemaining}
        doubleRemaining={doubleRemaining}
        shielded={shielded}
        kills={kills}
      />
      <ComboBadge combo={combo} playerHue={playerHue} comboRemaining={comboRemaining} />
      <Leaderboard rows={leaderboard} />
      <KillFeed />
      <InGameRoomCode />
      <DailyWinnerClock />
      <ModeBadge />
      <RewardPokeToast />
      {/* RewardUnlockedModal + RewardAnticipationToast removed per
          feedback — they interrupted active gameplay. The 60s reward
          still fires silently in the engine: the gold border appears
          on the player snake and a 🏆 chip lights up in the
          IdentityCard, which together communicate the unlock without
          a modal demanding a click. */}

      {/* Tiny FPS + mute icon + theme toggle + audio-locked hint.
          Hidden on mobile (< md) — the player can flip mute/theme via
          Settings; FPS is dev-debug noise that just steals room from
          the joystick on a phone. The unlock hint shows centred via a
          mobile-only sibling below if it ever fires. */}
      <div className="hidden md:flex absolute bottom-3 left-3 items-center gap-2 text-[10px] text-white/40 tabular-nums">
        <span>{fps.toFixed(0)} fps</span>
        <motion.button
          onClick={() => toggleMute?.()}
          whileHover={{ scale: 1.08 }}
          whileTap={{ scale: 0.94 }}
          transition={{ type: 'spring', stiffness: 460, damping: 18 }}
          className="pointer-events-auto px-1.5 py-0.5 rounded border border-white/15 hover:border-white/40 transition-colors uppercase tracking-wider"
          title="Mute (M)"
        >
          {muted ? '🔇 mute' : '🔊 audio'}
        </motion.button>
        <ThemeToggleHud />
        <AnimatePresence>
          {audioLocked && !muted && (
            <motion.span
              initial={{ opacity: 0, x: -6 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -6, transition: { duration: 0.15 } }}
              transition={{ duration: 0.25 }}
              className="text-white/55 italic normal-case tracking-normal"
            >
              click anywhere to enable sound
            </motion.span>
          )}
        </AnimatePresence>
      </div>

      {/* Controls hint — desktop only, auto-fades after 6s. The pause
          menu (Esc) is the canonical place to see the full list. */}
      <ControlsHint />

      {/* First-play onboarding overlay. Shows once per device. */}
      <FirstPlayTutorial />

      {/* Mass milestone celebrations — popped by server `milestone`
          messages when player crosses 1K / 2.5K / 5K / 10K / 25K mass. */}
      <MilestoneToast />
      <SurvivalToast />
      <HapticsHub />

      {/* Mobile-only touch controls — bottom-right pair. Boost is held,
          Eject is a tap. Visible only on coarse pointers (touch screens). */}
      <MobileControls alive={alive} />

      {/* Achievement toast — bottom-center, slides up + fades after 3.5s. */}
      <AnimatePresence>
        {toastAch && (
          <motion.div
            key={toastAch.id}
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 380, damping: 26 }}
            className="absolute bottom-16 left-1/2 -translate-x-1/2 hud-panel px-4 py-3 flex items-center gap-3 pointer-events-none z-20"
            role="status"
            aria-live="polite"
          >
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center text-base"
              style={{
                color: `hsl(${toastAch.hue} 100% 70%)`,
                background: `radial-gradient(circle, hsla(${toastAch.hue},100%,55%,0.25) 0%, transparent 75%)`,
                boxShadow: `0 0 14px hsla(${toastAch.hue},100%,55%,0.5)`,
                border: `1px solid hsla(${toastAch.hue},100%,55%,0.5)`,
              }}
            >
              {toastAch.glyph}
            </div>
            <div className="text-left">
              <div
                className="text-[9px] uppercase tracking-[0.25em]"
                style={{ color: `hsl(${toastAch.hue} 100% 75%)` }}
              >
                achievement
              </div>
              <div className="text-sm font-semibold text-white">
                {toastAch.name}
              </div>
              <div className="text-[10px] text-white/55">
                {toastAch.description}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Surge boost vignette — cyan edge glow while the free-boost Surge
          powerup is active. Pulses gently so the player feels the timer. */}
      <AnimatePresence>
        {surgeRemaining > 0 && (
          <motion.div
            className="nx-surge-vignette"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
          />
        )}
      </AnimatePresence>

      {/* Pause menu — full menu with Resume / Home / controls reference.
          Triggered by Esc (handled in InputManager → engine.togglePause). */}
      <AnimatePresence>
        {paused && alive && (
          <motion.div
            className="absolute inset-0 flex items-center justify-center bg-black/55 backdrop-blur-sm pointer-events-auto z-20"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
          >
            <motion.div
              className="hud-panel relative px-7 py-6 text-center min-w-[260px]"
              initial={{ scale: 0.92, y: 6 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.94, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 420, damping: 28 }}
            >
              <div
                className="text-xs tracking-[0.3em] uppercase mb-1"
                style={{ color: `hsl(${playerHue} 100% 70%)` }}
              >
                paused
              </div>
              <PauseRoomCode />
              <div className="flex flex-col gap-2 mb-4 text-[10px] text-white/60">
                <div className="flex items-center justify-between gap-6">
                  <span className="text-white/85 font-mono">MOUSE</span>
                  <span>steer</span>
                </div>
                <div className="flex items-center justify-between gap-6">
                  <span className="text-white/85 font-mono">SPACE</span>
                  <span>boost</span>
                </div>
                <div className="flex items-center justify-between gap-6">
                  <span className="text-white/85 font-mono">W</span>
                  <span>eject</span>
                </div>
                <div className="flex items-center justify-between gap-6">
                  <span className="text-white/85 font-mono">M</span>
                  <span>mute</span>
                </div>
                <div className="flex items-center justify-between gap-6">
                  <span className="text-white/85 font-mono">ESC</span>
                  <span>pause</span>
                </div>
              </div>
              <div className="flex items-center justify-center gap-3">
                <button
                  onClick={() => useGameStore.getState().togglePause?.()}
                  className="px-3 py-1.5 rounded text-[10px] uppercase tracking-[0.2em] bg-white/8 hover:bg-white/15 text-white/85 transition-colors"
                >
                  Resume
                </button>
                {onHome && (
                  <button
                    onClick={() => {
                      useGameStore.getState().togglePause?.();
                      onHome();
                    }}
                    className="text-[10px] uppercase tracking-[0.2em] text-white/55 hover:text-white/85 transition-colors"
                  >
                    ← home
                  </button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <DeathRecapOverlay />

      {/* DeathModal is suppressed while the recap cinematic is playing —
          the overlay owns the screen for that ~1.5s window. Engine sets
          recapActive=false (timer or Skip) and the modal pops naturally. */}
      {/* Surfaces WS drops so the player doesn't think the game crashed
          and close the tab. Sits above the canvas, below the death modal
          (z-40 vs death modal's higher z) so death still takes priority
          if both happen at once. */}
      <ReconnectingModal onHome={onHome} />

      <DeathModal
        alive={alive || recapActive}
        mass={mass}
        playerHue={playerHue}
        respawn={respawn}
        onHome={onHome}
        kills={kills}
        bestMass={bestMass}
        killedByName={killedByName}
        killedByHue={killedByHue}
        rank={recapRank}
        totalPlayers={leaderboard.length}
        topThree={leaderboard.slice(0, 3).map((r) => ({
          name: r.name,
          mass: r.mass,
          hue: r.hue,
          isPlayer: r.isPlayer,
        }))}
      />
    </div>
  );
}

/** Right-mid keybindings cheatsheet. Briefly visible after spawn for
 *  newcomers, then fades — once you've played for 6 seconds you know
 *  what the keys do; persistent text is noise after that point. The
 *  pause menu (Esc) is the canonical place to look it up again. */
function ControlsHint() {
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setVisible(false), 6000);
    return () => clearTimeout(t);
  }, []);
  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, x: 8 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 8, transition: { duration: 0.4 } }}
          transition={{ duration: 0.35, delay: 0.2 }}
          className="hud-panel absolute top-1/2 -translate-y-1/2 right-3 px-2.5 py-2 text-[9px] text-white/60 hidden md:block"
        >
          <div className="flex items-baseline gap-1.5"><span className="text-white/80 w-10">MOUSE</span><span className="text-white/40">steer</span></div>
          <div className="flex items-baseline gap-1.5"><span className="text-white/80 w-10">SPACE</span><span className="text-white/40">boost</span></div>
          <div className="flex items-baseline gap-1.5"><span className="text-white/80 w-10">W</span><span className="text-white/40">eject</span></div>
          <div className="flex items-baseline gap-1.5"><span className="text-white/80 w-10">ESC</span><span className="text-white/40">pause</span></div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/** Bottom-left theme toggle — lets the player flip dark/light WITHOUT
 *  leaving the game. The engine's GameCanvas subscribes to the store's
 *  theme key and live-flips the canvas palette, so the world updates
 *  immediately. Icon mirrors the *next* state (sun = "click for light"
 *  when in dark, moon = "click for dark" when in light), matching the
 *  landing's FloatingUtilityBar convention. */
function ThemeToggleHud() {
  const theme = useGameStore((s) => s.theme);
  const toggleTheme = useGameStore((s) => s.toggleTheme);
  const isDark = theme === 'dark';
  return (
    <motion.button
      onClick={toggleTheme}
      whileHover={{ scale: 1.08 }}
      whileTap={{ scale: 0.94 }}
      transition={{ type: 'spring', stiffness: 460, damping: 18 }}
      className="pointer-events-auto px-1.5 py-0.5 rounded border border-white/15 hover:border-white/40 transition-colors uppercase tracking-wider flex items-center gap-1"
      title={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
      aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
    >
      <span aria-hidden>{isDark ? '☀' : '🌙'}</span>
      <span>{isDark ? 'light' : 'dark'}</span>
    </motion.button>
  );
}

/** Top-center arena chip — visible during play. Shows the friendly
 *  arena name + live human count, with the share-code on click. Only
 *  renders when the realtime server is connected. */
function InGameRoomCode() {
  const code = useGameStore((s) => s.netRoomCode);
  const arenaName = useGameStore((s) => s.netArenaName);
  const humans = useGameStore((s) => s.netPlayers);
  const status = useGameStore((s) => s.netStatus);
  if (!code || status !== 'connected') return null;
  const copy = async (): Promise<void> => {
    try { await navigator.clipboard.writeText(code); } catch { /* noop */ }
  };
  // Top-20 clears the DailyWinnerClock (two-line, ~64px from top-3).
  return (
    <button
      onClick={copy}
      className="absolute top-20 left-1/2 -translate-x-1/2 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/12 text-[10px] uppercase tracking-[0.22em] text-white/65 hover:text-white hover:bg-white/10 transition-colors pointer-events-auto"
      title="Click to copy room code"
    >
      {/* LIVE pip — emerald, pulsing — so the chip reads as "real-time" */}
      <span className="relative flex h-1.5 w-1.5">
        <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
        <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400" />
      </span>
      {arenaName && (
        <span className="text-white/85 font-semibold tracking-[0.18em]">
          {arenaName}
        </span>
      )}
      {humans > 0 ? (
        <span className="text-white/50 tabular-nums">
          · {humans} live
        </span>
      ) : (
        <span className="text-amber-200/70 tabular-nums">
          · warming
        </span>
      )}
      <span className="text-white/30 font-mono tracking-[0.2em] text-[9px]">
        {code}
      </span>
    </button>
  );
}

/** Inside the pause modal: surface the realtime room code so the player
 *  can share a join link with friends. Only renders when connected. */
function PauseRoomCode() {
  const netRoomCode = useGameStore((s) => s.netRoomCode);
  if (!netRoomCode) return null;
  const copy = async (): Promise<void> => {
    try { await navigator.clipboard.writeText(netRoomCode); } catch { /* noop */ }
  };
  return (
    <button
      onClick={copy}
      className="mx-auto mb-4 inline-flex items-center gap-1.5 text-[9px] uppercase tracking-[0.25em] text-white/55 hover:text-white transition-colors"
      title="Click to copy room code"
    >
      room <span className="font-mono text-white/85 tracking-[0.2em]">{netRoomCode}</span>
    </button>
  );
}

/** Mode badge — tiny pill below the daily-winner clock at the top-
 *  center showing which arena the player is in (LIVE / SPECTATING /
 *  TRAINING). Set by the MatchmakingGate on Play resolution. The
 *  badge stays read-only here; the "switch to live" upgrade affordance
 *  is reachable via the pause menu or the Home → Play flow. */
function ModeBadge() {
  const gameMode = useGameStore((s) => s.gameMode);
  // Don't render in 'live' mode — the absence of a badge IS the live
  // signal. Showing "LIVE" would just be noise on every frame; the
  // badge is reserved for the two states the player should NOTICE
  // (training = no live competition, spectating = can't play yet).
  if (gameMode === 'live') return null;

  const isTraining = gameMode === 'training';
  const color = isTraining ? '#ffd23d' : '#22e6ff';
  const label = isTraining ? 'training arena' : 'spectating live';
  const sub = isTraining
    ? 'solo · bots only'
    : 'auto-joins when a seat opens';
  return (
    <div
      className="absolute top-32 left-1/2 -translate-x-1/2 z-10 pointer-events-none"
      role="status"
      aria-live="polite"
    >
      <div
        className="flex items-center gap-2 px-3 py-1.5 rounded-full backdrop-blur-md"
        style={{
          background: `${color}1a`,
          border: `1px solid ${color}55`,
          boxShadow: `0 0 14px ${color}33`,
        }}
      >
        <span
          className="w-1.5 h-1.5 rounded-full"
          style={{ background: color, boxShadow: `0 0 5px ${color}` }}
        />
        <span
          className="text-[10px] uppercase tracking-[0.25em] font-semibold"
          style={{ color }}
        >
          {label}
        </span>
        <span className="text-white/30">·</span>
        <span className="text-[9px] uppercase tracking-[0.2em] text-white/55">
          {sub}
        </span>
      </div>
    </div>
  );
}

/** Mobile-only on-screen controls. Layout (per operator feedback —
 *  "buttons should sit at the bottom so mobile play is comfortable"):
 *
 *    ◄───── LEFT  ─────►          ◄────  EJECT ─►
 *                                  ◄────  BOOST ─►
 *
 *  Left-side thumb-zone: a big arc with ◀ and ▶ turn pads. While held,
 *  the snake gradually rotates in that direction (driven by
 *  InputManager.virtualHeading). Boost + Eject stay on the right thumb
 *  side as standard.
 *
 *  Hidden on hover-capable devices (desktops).
 */
/** Robust mobile/touch detection. The previous `[@media(hover:hover)]:
 *  hidden` Tailwind variant was missing real users — a phone with a
 *  Bluetooth mouse or "Request desktop site" reports `hover: hover`
 *  and the buttons silently never rendered. This JS check evaluates
 *  THREE independent signals and shows the buttons if ANY matches:
 *    - viewport ≤ 1024 px (covers phones + most tablets)
 *    - touchscreen capability (`ontouchstart` in window)
 *    - non-zero `navigator.maxTouchPoints` (modern API)
 *  Re-evaluated on resize so a rotated device or laptop hybrid that
 *  flips between tablet/laptop modes picks up the new state. */
function useShowMobileControls(): boolean {
  const compute = (): boolean => {
    if (typeof window === 'undefined') return false;
    const small = window.matchMedia('(max-width: 1024px)').matches;
    const touch = 'ontouchstart' in window ||
                  (navigator.maxTouchPoints !== undefined && navigator.maxTouchPoints > 0);
    return small || touch;
  };
  const [show, setShow] = useState<boolean>(compute);
  useEffect(() => {
    const onResize = (): void => setShow(compute());
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);
  return show;
}

function MobileControls({ alive }: { alive: boolean }) {
  const setBoost = useGameStore((s) => s.setBoost);
  const queueEject = useGameStore((s) => s.queueEject);
  const setMobileSteer = useGameStore((s) => s.setMobileSteer);
  const showMobile = useShowMobileControls();
  if (!alive) return null;
  if (!showMobile) return null;
  // Haptic feedback on supported devices. Short tap pulse on press —
  // subtle physical feedback that the button registered the press.
  const buzz = (ms: number): void => {
    try { navigator.vibrate?.(ms); } catch { /* unsupported */ }
  };

  return (
    <div className="absolute inset-0 pointer-events-none select-none">
      <VirtualJoystick
        onSteer={(angle) => setMobileSteer?.(angle)}
        onBoost={(on) => { if (on) buzz(10); setBoost?.(on); }}
      />

      {/* ── Eject — tiny chip top-center, rarely used */}
      <button
        type="button"
        aria-label="Eject"
        onPointerDown={(e) => { e.preventDefault(); buzz(15); queueEject?.(); }}
        className="pointer-events-auto rounded-full active:scale-90 transition-transform"
        style={{
          position: 'absolute',
          top: 'max(10px, env(safe-area-inset-top, 0px))',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 32,
          height: 32,
          border: '1.5px solid rgba(217,70,239,0.55)',
          background: 'rgba(217,70,239,0.18)',
          color: 'rgb(245,200,255)',
          fontSize: 10,
          fontFamily: 'monospace',
          fontWeight: 700,
          letterSpacing: '0.18em',
          touchAction: 'none',
          WebkitTapHighlightColor: 'transparent',
          userSelect: 'none',
        }}
      >
        W
      </button>
    </div>
  );
}

/** Virtual joystick — anchored bottom-CENTER, integrated boost.
 *
 *  Steering: drag the knob in any direction. The angle (atan2 of the
 *  drag vector, axis-aligned world space) is fed to the engine via
 *  onSteer. Release returns the knob to center; snake continues in its
 *  last heading (slither.io behavior).
 *
 *  BOOST: when the knob is pulled past 85 % of the max radius, boost
 *  fires automatically — the player just pulls harder in their target
 *  direction. Hysteresis (turns OFF at 70 %) prevents flicker at the
 *  threshold. Visual cue: the knob glows magenta + the base ring
 *  pulses outward while boosting.
 *
 *  Pointer-capture is critical: without it, the browser stops sending
 *  pointermove the moment the thumb leaves the touched element, and
 *  the snake reverts to straight. With capture, every pointermove
 *  routes to the joystick until release no matter where the thumb is.
 *
 *  Touch zone is the WHOLE BOTTOM HALF of the screen — generous fat-
 *  thumb landing without precision required.
 */
function VirtualJoystick({
  onSteer,
  onBoost,
}: {
  onSteer: (angle: number | null) => void;
  onBoost: (on: boolean) => void;
}) {
  const BASE_SIZE = 140;
  const KNOB_SIZE = 60;
  const MAX_RADIUS = 65;
  const BOOST_ON_PCT  = 0.85;   // pull past 85 % of max → boost on
  const BOOST_OFF_PCT = 0.70;   // ease back below 70 % → boost off (hysteresis)

  const ref = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(false);
  const [boosting, setBoosting] = useState(false);
  const [knob, setKnob] = useState({ x: 0, y: 0 });
  const originRef = useRef<{ x: number; y: number } | null>(null);
  // Held outside react state so the hysteresis check sees the live
  // value without a setState round-trip stall.
  const boostingRef = useRef(false);

  const compute = (clientX: number, clientY: number): void => {
    const origin = originRef.current;
    if (!origin) return;
    let dx = clientX - origin.x;
    let dy = clientY - origin.y;
    const dist = Math.hypot(dx, dy);
    if (dist > MAX_RADIUS) {
      dx = (dx / dist) * MAX_RADIUS;
      dy = (dy / dist) * MAX_RADIUS;
    }
    setKnob({ x: dx, y: dy });

    const mag = Math.hypot(dx, dy);
    if (mag < 8) {
      onSteer(null);
    } else {
      onSteer(Math.atan2(dy, dx));
    }

    // Boost detection with hysteresis. The clamped knob never exceeds
    // MAX_RADIUS, so we compare the RAW dist to detect the player
    // pulling hard against the edge — feels like pressing the
    // joystick "into" the ring.
    const pct = dist / MAX_RADIUS;
    if (!boostingRef.current && pct >= BOOST_ON_PCT) {
      boostingRef.current = true;
      setBoosting(true);
      onBoost(true);
    } else if (boostingRef.current && pct < BOOST_OFF_PCT) {
      boostingRef.current = false;
      setBoosting(false);
      onBoost(false);
    }
  };

  const onDown = (e: React.PointerEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    try { (e.currentTarget as Element).setPointerCapture(e.pointerId); } catch { /* ignore */ }
    originRef.current = { x: e.clientX, y: e.clientY };
    setActive(true);
    compute(e.clientX, e.clientY);
  };
  const onMove = (e: React.PointerEvent): void => {
    if (!active) return;
    e.preventDefault();
    compute(e.clientX, e.clientY);
  };
  const onUp = (e: React.PointerEvent): void => {
    try { (e.currentTarget as Element).releasePointerCapture(e.pointerId); } catch { /* ignore */ }
    setActive(false);
    setKnob({ x: 0, y: 0 });
    originRef.current = null;
    onSteer(null);
    if (boostingRef.current) {
      boostingRef.current = false;
      setBoosting(false);
      onBoost(false);
    }
  };

  // Default hint position = bottom-CENTER.
  const HINT_BOTTOM = 28;
  // Visual styling token shared by base + knob — magenta tint while
  // boosting so the player gets a clear "yes, you're boosting" cue.
  const accentRing = boosting
    ? 'rgba(255,90,210,0.85)'
    : 'rgba(94,216,255,0.55)';
  const accentGlow = boosting
    ? 'rgba(255,90,210,0.60)'
    : 'rgba(94,216,255,0.30)';
  const knobGrad = boosting
    ? 'radial-gradient(circle at 30% 30%, rgba(255,210,250,0.95), rgba(255,90,210,0.75) 70%, rgba(195,30,170,0.50))'
    : 'radial-gradient(circle at 30% 30%, rgba(180,235,255,0.95), rgba(94,216,255,0.65) 70%, rgba(34,150,210,0.45))';
  const knobBorder = boosting ? 'rgba(255,200,245,0.90)' : 'rgba(180,235,255,0.85)';
  const knobShadow = boosting
    ? '0 0 32px rgba(255,90,210,0.95), inset 0 -4px 8px rgba(0,0,0,0.20), inset 0 2px 4px rgba(255,255,255,0.30)'
    : '0 0 24px rgba(94,216,255,0.85), inset 0 -4px 8px rgba(0,0,0,0.20), inset 0 2px 4px rgba(255,255,255,0.30)';

  return (
    <>
      {/* Touch zone — bottom HALF of the screen so the player can grab
          the joystick from anywhere on the lower part of the canvas. */}
      <div
        ref={ref}
        className="pointer-events-auto"
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onUp}
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          height: '55%',
          touchAction: 'none',
          WebkitTapHighlightColor: 'transparent',
        }}
        aria-label="Steering joystick"
      />

      {/* Decorative base hint — at bottom-center by default; jumps to
          the touch point while active. Has a tiny "BOOST" label that
          fades in when the knob hits the boost threshold. */}
      <div
        className="pointer-events-none"
        style={{
          position: 'absolute',
          left: active && originRef.current
            ? originRef.current.x - BASE_SIZE / 2
            : `calc(50% - ${BASE_SIZE / 2}px)`,
          top: active && originRef.current
            ? originRef.current.y - BASE_SIZE / 2
            : undefined,
          bottom: active && originRef.current ? undefined : HINT_BOTTOM,
          width: BASE_SIZE,
          height: BASE_SIZE,
          borderRadius: '50%',
          border: `2px solid ${accentRing}`,
          background: 'radial-gradient(circle at 30% 30%, rgba(94,216,255,0.10), rgba(94,216,255,0.04) 70%, transparent 100%)',
          boxShadow: `0 0 22px ${accentGlow}, inset 0 0 20px ${accentGlow}`,
          opacity: active ? 1 : 0.62,
          transition: 'opacity 0.18s ease-out, border-color 0.12s, box-shadow 0.12s',
        }}
      />
      {/* Knob */}
      <div
        className="pointer-events-none"
        style={{
          position: 'absolute',
          left: (active && originRef.current ? originRef.current.x : '50%') as string | number,
          marginLeft: active && originRef.current ? 0 : -KNOB_SIZE / 2,
          transform: `translate(${active && originRef.current ? -KNOB_SIZE / 2 + knob.x : knob.x}px, ${active && originRef.current ? -KNOB_SIZE / 2 + knob.y : 0}px)`,
          top: active && originRef.current ? originRef.current.y : undefined,
          bottom: active && originRef.current ? undefined : HINT_BOTTOM + BASE_SIZE / 2 - KNOB_SIZE / 2 - knob.y,
          width: KNOB_SIZE,
          height: KNOB_SIZE,
          borderRadius: '50%',
          background: knobGrad,
          border: `2px solid ${knobBorder}`,
          boxShadow: knobShadow,
          opacity: active ? 1 : 0.85,
          transition: active ? 'background 0.14s, border-color 0.14s, box-shadow 0.14s' : 'left 0.18s ease-out, bottom 0.18s ease-out, top 0.18s ease-out, opacity 0.18s, background 0.14s, border-color 0.14s, box-shadow 0.14s',
        }}
      />
      {/* "BOOST" label that appears when the player is pulling hard —
          a learning aid so they discover the gesture exists. Fades in
          along the bottom of the base ring. */}
      <div
        className="pointer-events-none text-center"
        style={{
          position: 'absolute',
          left: active && originRef.current
            ? originRef.current.x - 50
            : `calc(50% - 50px)`,
          top: active && originRef.current ? originRef.current.y + BASE_SIZE / 2 - 8 : undefined,
          bottom: active && originRef.current ? undefined : HINT_BOTTOM - 14,
          width: 100,
          fontSize: 9,
          fontWeight: 800,
          letterSpacing: '0.32em',
          color: boosting ? 'rgb(255,200,245)' : 'rgba(255,255,255,0.50)',
          textShadow: boosting ? '0 0 10px rgba(255,90,210,0.90)' : 'none',
          opacity: active ? (boosting ? 1 : 0.85) : 0.45,
          transition: 'opacity 0.12s, color 0.12s, text-shadow 0.12s',
          fontFamily: '"Inter", system-ui, sans-serif',
        }}
      >
        {boosting ? '⚡ BOOST' : 'pull to boost'}
      </div>
    </>
  );
}
